import { useCallback, useState, type ReactNode } from "react";
import { Button, Dialog } from "@omnabase/ui";
import { websiteCapSentence, websiteReadLine, websiteSearchedParts } from "@omnabase/shared";
import { useSession } from "../../shell/store";
import { useNav, useWs } from "../../lib/nav";
import { useCan } from "../../lib/access";
import { kb, type KbSource, type Scope } from "../../lib/knowledge";
import { PathField } from "./PathField";

/** A website's dialog "Choose what is read…" (the send-and-sections round, 2026-10-23; brief part 5: "the dialog says nothing about
 *  sections"), named after the source, its intro the row's read line: first "Searched when needed", the forum parts as plain addresses, each
 *  with the one-way "Read instead" (absent when there are none); then "Leave out", the paths the read skips, each with Remove, the field "Add a
 *  path" with the site's paths as suggestions, and "Sync now" (a path taken off comes back at the next sync). When the plan's words stopped the
 *  read, the cap line in words with the way up, for whoever may buy. Only workspace administrators change it (the API refuses the rest).
 *  Its address is `?site=<source id>`. */
type Words = { used: number; cap: number };
export function SiteDialog({ scope, source, liveSearch, words, onClose, onChanged }: { scope: Scope; source: KbSource; liveSearch: boolean; words: Words; onClose: () => void; onChanged: () => void }) {
  const { slug } = useWs(); const { toast, workspace } = useSession(); const can = useCan();
  const admin = workspace.me.role === "owner" || can("team"); // the web's "Admins"
  const [busy, setBusy] = useState(false); const [reading, setReading] = useState<string[]>([]); // forum parts switched to reading: their button gives way at once
  const cap = can("billing") ? websiteCapSentence(source, words) : null;
  const act = async (what: () => Promise<unknown>, done: string): Promise<boolean> => {
    setBusy(true);
    try { await what(); onChanged(); toast(done, { kind: "success" }); return true; } catch (e) { toast((e as Error).message, { kind: "error" }); return false; } finally { setBusy(false); }
  };
  const paths = source.excludedPaths ?? [];
  const setPaths = (next: string[], done: string) => act(() => kb.setExcludedPaths(slug, scope.id, source.id, next), done);
  return (
    <Dialog title={source.name} intro={websiteReadLine(source, { liveSearch }) ?? undefined} width={640} onClose={onClose} footer={<Button onClick={onClose}>Close</Button>}>
      {cap && <p className="ob-site-read__cap">{cap} <UpgradeLink label="Upgrade" plain={null} /></p>}
      <SearchedParts source={source} liveSearch={liveSearch} admin={admin} busy={busy} reading={reading} onRead={(path) => { setReading((r) => [...r, path]); void act(() => kb.readForumInstead(slug, scope.id, source.id, path), "The forum is read with the next sync").then((ok) => { if (!ok) setReading((r) => r.filter((x) => x !== path)); }); }} />
      <div className="ob-leave-out">
        <h3 className="ob-site-read__title">Leave out</h3>
        <ul className="ob-leave-out__list" aria-label="Leave out">
          {paths.map((p) => (
            <li key={p} className="ob-leave-out__item"><code>{p}</code>
              {admin && <Button size="sm" variant="ghost" disabled={busy} aria-label={`Remove ${p}`} onClick={() => void setPaths(paths.filter((x) => x !== p), `${p} comes back with the next sync`)}>Remove</Button>}
            </li>
          ))}
        </ul>
        {paths.length === 0 && <p className="ob-form-row__help">Nothing is left out. A path leaves out every page under it.</p>}
        {admin && <LeaveOutTools source={source} scope={scope} paths={paths} busy={busy} onAdd={(p) => setPaths([...paths, p], `${p} is left out`)} onSync={() => void act(() => kb.syncSource(slug, scope.id, source.id), "Sync queued")} />}
      </div>
    </Dialog>
  );
}

/** the forum parts searched when needed, each with "Read instead" for an administrator (one way: once clicked, the button gives way to "Read
 *  with the next sync" and never comes back); nothing when there are none */
function SearchedParts({ source, liveSearch, admin, busy, reading, onRead }: { source: KbSource; liveSearch: boolean; admin: boolean; busy: boolean; reading: string[]; onRead: (path: string) => void }) {
  const parts = websiteSearchedParts(source, { liveSearch });
  if (!parts.length) return null;
  return (
    <div className="ob-site-read__searched">
      <h3 className="ob-site-read__title">Searched when needed</h3>
      <ul className="ob-leave-out__list" aria-label="Searched when needed">
        {parts.map((p) => <li key={p.path} className="ob-leave-out__item"><span>{p.address}</span>{reading.includes(p.path) ? <span className="ob-form-row__help">Read with the next sync</span> : admin && <Button size="sm" disabled={busy} onClick={() => onRead(p.path)}>Read instead</Button>}</li>)}
      </ul>
    </div>
  );
}

/** the field that adds a path (with the site's paths as suggestions) and "Sync now" */
function LeaveOutTools({ source, scope, paths, busy, onAdd, onSync }: { source: KbSource; scope: Scope; paths: string[]; busy: boolean; onAdd: (path: string) => Promise<boolean>; onSync: () => void }) {
  const { slug } = useWs();
  const suggest = useCallback(async (q: string) => (await kb.suggestPaths(slug, scope.id, source.id, q)).paths.filter((p) => !paths.includes(p)), [slug, scope.id, source.id, paths]);
  return (
    <div className="ob-leave-out__tools">
      <PathField suggest={suggest} disabled={busy} onAdd={onAdd} />
      <Button size="sm" variant="ghost" icon="sync" disabled={busy || source.sync.state === "queued" || source.sync.state === "syncing"} onClick={onSync}>Sync now</Button>
    </div>
  );
}

/** the way up to the plans for someone who may buy (the owner and whoever holds billing); for the rest `plain` (the word itself by default) */
export function UpgradeLink({ label, plain = label }: { label: string; plain?: ReactNode }) {
  const can = useCan(); const nav = useNav(); const { ws } = useWs();
  if (!can("billing")) return <>{plain}</>;
  const to = ws("/settings/billing?plans=1");
  return <a className="ob-link" href={to} onClick={(e) => nav(to, e)}>{label}</a>;
}

/** the Source cell's read line in pages, and for whoever may buy the cap line in words with "upgrade for the rest" the way up */
export function ReadLine({ source, liveSearch, words }: { source: KbSource; liveSearch: boolean; words: Words }): ReactNode {
  const can = useCan();
  const line = websiteReadLine(source, { liveSearch });
  if (!line) return null;
  const cap = can("billing") ? websiteCapSentence(source, words) : null;
  if (!cap) return line;
  return <>{line} · {cap.replace(/upgrade for the rest$/, "")}<UpgradeLink label="upgrade for the rest" /></>;
}
